import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { useCallback, useEffect, useRef } from "react";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import ToolbarPlugin from "./ToolbarPlugin";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { EditorState } from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot } from "lexical";
import { HeadingNode } from "@lexical/rich-text";
import { ScrollArea } from "../ui/scroll-area";

const theme = {
  paragraph: "mb-1",


};

export default function LexicalEditor({
  value,
  onChange,
  placeholder = "Enter text here...",
  disabled = false,
  onKeyDown, 
}: {
  value: any;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}) {
  // Determine if value is HTML content
  const isHtmlContent = typeof value === 'string' && 
    (value.startsWith('<') || value.includes('<p') || value.includes('<div'));

  const initialConfig = {
    namespace: "ProductDescriptionEditor",
    theme,
    onError: (error: any) => console.error(error),
    nodes: [ListNode, ListItemNode, LinkNode, HeadingNode],

    ...(value && !isHtmlContent ? { editorState: value } : {}),
  };

  return (
    <div className="border rounded-md">
      <LexicalComposer initialConfig={initialConfig}>
        <EditorContent 
          onChange={onChange} 
          placeholder={placeholder} 
          disabled={disabled} 
          initialHtml={isHtmlContent ? value : undefined} 
          onKeyDown={onKeyDown} 
        />
      </LexicalComposer>
    </div>
  );
}

function EditorContent({
  onChange,
  placeholder,
  disabled,
  initialHtml,
  onKeyDown, 
}: {
  onChange: (html: string) => void;
  placeholder: string;
  disabled: boolean;
  initialHtml?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void; 
}) {
  const [editor] = useLexicalComposerContext();
  const initialValueAppliedRef = useRef(false);

  // Only apply the initial HTML once when the editor is first loaded
  useEffect(() => {
    if (initialHtml && editor && !initialValueAppliedRef.current) {
      editor.update(() => {
        const parser = new DOMParser();
        const dom = parser.parseFromString(initialHtml, 'text/html');
        const nodes = $generateNodesFromDOM(editor, dom);
        $getRoot().clear();
        $getRoot().append(...nodes);
        initialValueAppliedRef.current = true;
      });
    }
  }, [editor, initialHtml]);

  const handleChange = useCallback(
    (editorState: EditorState) => {
      editorState.read(() => {
        const html = $generateHtmlFromNodes(editor);
        onChange(html); 
      });
    },
    [editor, onChange]
  );

  return (
    <div className="editor-container relative">
      <ToolbarPlugin />
      <ScrollArea className="h-64 py-0 px-0">
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className="editor-input p-3 min-h-[200px] outline-none relative"
            disabled={disabled}
            onKeyDown={onKeyDown} 
          />
        }
        placeholder={
          <div className="editor-placeholder text-muted-foreground absolute top-[13px] left-[12px] pointer-events-none">
            {placeholder}
          </div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <HistoryPlugin />
      <AutoFocusPlugin />
      <LinkPlugin />
      <ListPlugin />
      <OnChangePlugin onChange={handleChange} />
      </ScrollArea>
    </div>
  );
}
